/**
 * Comment store — pure, immutable operations over a `DiagramComment[]` list.
 *
 * Comments are document-level annotation threads (like layers): they live as an
 * array on `DiagramData` and round-trip through serialize/parse. Keeping the
 * actions as pure list transforms (rather than baking them into the React Flow
 * zundo store) makes them trivially unit-testable and lets both the editor and
 * the CRDT room reuse the same logic.
 *
 * Every action returns a NEW array (the input is never mutated); actions whose
 * target id is missing return the original list unchanged.
 */

import type { DiagramComment, DiagramCommentReply, Position } from '../types/schema'

/** Generate a reasonably-unique id, matching the editor's layer-id convention. */
function genId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

export interface CreateCommentInput {
  id?: string
  author: string
  text: string
  anchor?: Position
  nodeId?: string
  createdAt?: string
}

/** Build a fully-formed comment from minimal input. */
export function createComment(input: CreateCommentInput): DiagramComment {
  const comment: DiagramComment = {
    id: input.id ?? genId('comment'),
    author: input.author,
    text: input.text,
    resolved: false,
    createdAt: input.createdAt ?? new Date().toISOString(),
    replies: [],
  }
  if (input.anchor) comment.anchor = input.anchor
  if (input.nodeId) comment.nodeId = input.nodeId
  return comment
}

/** Append a comment to the list (immutably). */
export function addComment(list: DiagramComment[], comment: DiagramComment): DiagramComment[] {
  return [...list, comment]
}

/** Mark a thread resolved. */
export function resolveComment(list: DiagramComment[], id: string): DiagramComment[] {
  return list.map((c) => (c.id === id ? { ...c, resolved: true } : c))
}

/** Clear the resolved flag on a thread. */
export function unresolveComment(list: DiagramComment[], id: string): DiagramComment[] {
  return list.map((c) => (c.id === id ? { ...c, resolved: false } : c))
}

/** Toggle a thread's resolved state. */
export function toggleResolved(list: DiagramComment[], id: string): DiagramComment[] {
  return list.map((c) => (c.id === id ? { ...c, resolved: !c.resolved } : c))
}

/** Remove a thread by id. */
export function deleteComment(list: DiagramComment[], id: string): DiagramComment[] {
  return list.filter((c) => c.id !== id)
}

/** Replace the body text of a thread. */
export function updateCommentText(list: DiagramComment[], id: string, text: string): DiagramComment[] {
  return list.map((c) => (c.id === id ? { ...c, text } : c))
}

/** Reposition a free-floating comment's anchor. */
export function moveComment(list: DiagramComment[], id: string, anchor: Position): DiagramComment[] {
  return list.map((c) => (c.id === id ? { ...c, anchor } : c))
}

export interface AddReplyInput {
  id?: string
  author: string
  text: string
  createdAt?: string
}

/** Append a reply to a thread. */
export function addReply(
  list: DiagramComment[],
  commentId: string,
  reply: AddReplyInput,
): DiagramComment[] {
  const full: DiagramCommentReply = {
    id: reply.id ?? genId('reply'),
    author: reply.author,
    text: reply.text,
    createdAt: reply.createdAt ?? new Date().toISOString(),
  }
  return list.map((c) =>
    c.id === commentId ? { ...c, replies: [...(c.replies ?? []), full] } : c,
  )
}

/** Remove a reply from a thread. */
export function deleteReply(
  list: DiagramComment[],
  commentId: string,
  replyId: string,
): DiagramComment[] {
  return list.map((c) =>
    c.id === commentId
      ? { ...c, replies: (c.replies ?? []).filter((r) => r.id !== replyId) }
      : c,
  )
}
